import { Models } from '@core/types';
import { Item } from '@core/types/models';
import React, { useState, FunctionComponent, useEffect, useMemo } from 'react';

interface IProps {
  id: string;
  placeholder?: string;
  includeSelectedItem?: boolean;
  onChange: (value: string) => void;
  options?: Item[];
  value: string;
  valid?: boolean;
  asRow?: boolean;
  previewImages?: Models.TitleImage[];
}

const getImageUrl = (previewImages?: Models.TitleImage[], key?: string): string | undefined => {
  const image = previewImages?.find((img) => img.key === key);
  if (image?.image.startsWith('~')) {
    return `${process.env.REACT_APP_MAIN_SITE}${image.image.slice(1)}`;
  }
  return image?.image;
};

const Dropdown: FunctionComponent<IProps> = ({
  id,
  placeholder = '',
  includeSelectedItem,
  options,
  onChange,
  value,
  valid = true,
  previewImages,
}) => {
  const firstValue = options?.find((x) => x.key === value);
  const [isOpen, setIsOpen] = useState(false);
  const [_value, setValue] = useState(firstValue?.value || placeholder);
  const [previewImage, setPreviewImage] = useState<string | undefined>(getImageUrl(previewImages, _value));

  useEffect(() => {
    const body = document.querySelector('body');
    if (!body) {
      return;
    }

    body.addEventListener('click', _close);

    return () => body.removeEventListener('click', _close);
  }, []);

  const _setIsOpen = (e: React.MouseEvent) => {
    e.stopPropagation();

    setIsOpen(!isOpen);
  };

  const _close = () => {
    setIsOpen(false);
  };

  const onChangeInternal = (item: Item) => {
    setValue(item.value);
    onChange(item.key);
    setPreviewImage(getImageUrl(previewImages, item.key));
    setIsOpen(false);
  };

  const items: Models.DropdownItem[] | undefined = useMemo(() => {
    let newOptions = options;

    if (!includeSelectedItem) {
      newOptions = newOptions?.filter((x) => x.value !== _value);
    }

    if (previewImages) {
      newOptions = newOptions?.map((x) => {
        let image = previewImages.find((img) => img.key === x.key);
        if (image?.image.startsWith('~')) {
          image.image = `${process.env.REACT_APP_MAIN_SITE}/${image.image.slice(1)}`;
        }
        return {
          ...x,
          image: image ? image.image : undefined,
        };
      });
    }

    return newOptions;
  }, [options, includeSelectedItem, _value, previewImages]);

  return (
    <div className={`form__control form__row`} data-placeholder-shown={_value === placeholder} id={id}>
      <div className={`form__dropdown ${isOpen ? 'active' : ''} ${!valid ? 'invalid' : ''}`}>
        <div className="form__dropdown-selected" onClick={_setIsOpen}>
          <span dangerouslySetInnerHTML={{ __html: _value || placeholder }} />
          {previewImage && <img src={previewImage} alt="" role="presentation" />}
        </div>
        <div className="form__dropdown-values">
          {items?.map((x) => (
            <div key={x.key} className="form__dropdown-value" onClick={() => onChangeInternal(x)}>
              <span dangerouslySetInnerHTML={{ __html: x.value }} />
            </div>
          ))}
        </div>
      </div>
      {placeholder && <label className="form__label">{placeholder}</label>}
    </div>
  );
};

export default Dropdown;
